/**
 * Image selection coordination module
 * Provides a centralized way to select images and sync them across dealer/customer
 * Works in both standalone and integrated (runtime) modes — including when <e3d-stream>
 * is not rendered on the page (AVS-4369).
 */

import { createExperienceSync, IExperienceSyncChannel, IExperienceSyncState } from './experienceSync';
import { userSession } from 'state/session';
import type { IPublicSession } from 'state/session';
import { user } from 'utils/auth/user';
import { State } from 'utils/state';
import logger from 'utils/log';

type SendSyncFn = (data: { currentImageIndex: number }) => void;
type ScrollToFn = (index: number, instant: boolean) => void;

/**
 * Local, per-tab read model for the currently displayed 2D gallery index (AVS-4400).
 *
 * `currentImageIndex` used to live on `userSession()`, which is what AVS-4382 set out to
 * avoid: ExperienceSync is the wire owner of the index, and a session field mirroring it
 * trips `syncToServer`'s "no relevant keys changed" branch on every navigation, firing a
 * full-document `emitOnly` broadcast that a joined customer echoes straight back — the
 * mechanism behind AVS-4400. `syncStatus.ts`'s `degraded` flag is the existing precedent
 * for keeping exactly this kind of reactive-but-unsynced value off the session for the
 * same reason.
 *
 * `setCurrentImageIndex` stays the single writer (AVS-4382); it now writes here instead.
 */
export const imageSelectionState = new State<{ currentImageIndex: number }>({ currentImageIndex: 0 });

export function getCurrentImageIndex(): number {
  return imageSelectionState.get('currentImageIndex');
}

/**
 * A stale index must not leak from one session into the next tab lifetime (AVS-4400).
 * Mirrors the `endedAt`/`id` checks the standalone-channel teardown listener below already
 * uses, plus `meta.reset` for an explicit `resetSession()`.
 *
 * Called from `initEvents(state)` — NOT registered at this module's top level. `imageSelection.ts`
 * is imported (transitively, via `carImages.ts`) from `state/session.ts` itself before that
 * module finishes evaluating its own `state` module-scope binding; a top-level `userSession()`
 * call here would read that binding while still in its temporal dead zone. Taking `state`
 * as a parameter — already-constructed by the time `initEvents` runs — sidesteps the cycle
 * entirely.
 */
export function initImageSelectionSessionReset(state: State<IPublicSession>): void {
  state.on(
    'change',
    (
      changedKeys: string[],
      _values: unknown[],
      oldValues: unknown[],
      _values2: unknown,
      _state: unknown,
      meta?: { reset?: boolean }
    ) => {
      const sessionEnded = changedKeys.includes('endedAt') && !!state.get('endedAt');
      const idIndex = changedKeys.indexOf('id');
      // Only a transition away from a real id counts — the initial undefined -> id write
      // happens when the session POST resolves, which must not zero a selection made
      // during that window (PR #1785 review).
      const sessionIdChanged = idIndex !== -1 && oldValues[idIndex] !== undefined;
      if (meta?.reset || sessionEnded || sessionIdChanged) {
        imageSelectionState.setValues({ currentImageIndex: 0 });
      }
    }
  );
}

interface IImageSelectionRegistry {
  sendSync: SendSyncFn | null;
  scrollTo: ScrollToFn | null;
}

const registry: IImageSelectionRegistry = {
  sendSync: null,
  scrollTo: null,
};

// ---------------------------------------------------------------------------
// Standalone sync channel (AVS-4369)
//
// When <e3d-stream> is not rendered (and therefore Micro2DApp never mounts),
// registry.sendSync stays null and selectImage() would silently no-op.
// To fix this we create a minimal ExperienceSync channel on first use so
// that currentImageIndex is still broadcast to the customer in real-time.
//
// Lifecycle:
//   - Lazy-created on the first selectImage() call when the registry is empty.
//   - Torn down immediately when Micro2DApp registers itself (it takes over).
//   - Torn down on unregisterImageSelection() (Micro2DApp unmounted).
//   - Re-created automatically if a new session starts (sessionId changes).
// ---------------------------------------------------------------------------

let standaloneChannel: IExperienceSyncChannel<IExperienceSyncState> | null = null;
let standaloneChannelSessionId: string | undefined;
let unsubscribeSessionEnd: (() => void) | null = null;
// Index queued while the standalone channel is still connecting.
// Coalesces multiple rapid calls — only the latest index is sent on connect.
let pendingIndex: number | null = null;

function getOrCreateStandaloneChannel(): IExperienceSyncChannel<IExperienceSyncState> | null {
  const sessionId = userSession().get('id');
  if (!sessionId) return null;

  // Session changed — tear down the stale channel so a fresh one is created.
  if (standaloneChannel && standaloneChannelSessionId !== sessionId) {
    disconnectStandaloneChannel();
  }

  if (standaloneChannel) return standaloneChannel;

  standaloneChannelSessionId = sessionId;

  const channel = createExperienceSync<IExperienceSyncState>({
    sessionId,
    onConnect: () => {
      // Flush any index that arrived before the connection was ready.
      if (pendingIndex !== null) {
        channel.send({ currentImageIndex: pendingIndex });
        pendingIndex = null;
      }
    },
    onError: (err) => {
      logger.warn('[imageSelection] standalone channel error:', err);
    },
  });

  standaloneChannel = channel;

  // Disconnect as soon as the session ends so we don't hold a stale WebSocket open.
  unsubscribeSessionEnd = userSession().on('change', (changedKeys: string[]) => {
    if (changedKeys.includes('endedAt') || (changedKeys.includes('id') && !userSession().get('id'))) {
      disconnectStandaloneChannel();
    }
  });

  channel.connect().catch((err: unknown) => {
    logger.warn('[imageSelection] standalone channel connect failed:', err);
    standaloneChannel = null;
    standaloneChannelSessionId = undefined;
    pendingIndex = null;
  });

  return channel;
}

function disconnectStandaloneChannel(): void {
  unsubscribeSessionEnd?.();
  unsubscribeSessionEnd = null;
  if (standaloneChannel) {
    standaloneChannel.disconnect();
    standaloneChannel = null;
    standaloneChannelSessionId = undefined;
    pendingIndex = null;
  }
}

/**
 * Register the image selection handlers (called by Micro2DApp component).
 * Micro2DApp takes over sync responsibility; any standalone channel is torn down.
 */
export function registerImageSelection(sendSync: SendSyncFn, scrollTo: ScrollToFn): void {
  disconnectStandaloneChannel();
  registry.sendSync = sendSync;
  registry.scrollTo = scrollTo;
}

/**
 * Unregister handlers when component unmounts.
 */
export function unregisterImageSelection(): void {
  disconnectStandaloneChannel();
  registry.sendSync = null;
  registry.scrollTo = null;
}

/**
 * Clamp an index to a valid position for a gallery of `length` items, falling back to 0
 * when out of range. Shared by `showImages()` (state/session/carImages.ts) and Micro2DApp's
 * asset-reset effect so the two out-of-range fallbacks can't drift apart (AVS-4382 review).
 */
export function clampImageIndex(index: number, length: number): number {
  return index >= 0 && index < length ? index : 0;
}

/**
 * Mirror the given index into the local read model (AVS-4382, moved off the session in
 * AVS-4400). ExperienceSync is the source of truth for the value; this is the only place
 * that copies it into `imageSelectionState`.
 */
export function setCurrentImageIndex(index: number): void {
  imageSelectionState.setValues({ currentImageIndex: index });
}

/**
 * Select a specific image by index and sync to the customer.
 * Works regardless of whether <e3d-stream> is rendered on the page.
 * @param index - Zero-based image index
 */
export function selectImage(index: number): void {
  // Always update session state so the index is tracked locally and persisted.
  setCurrentImageIndex(index);

  if (registry.sendSync) {
    // Micro2DApp is mounted: delegate to its WebRTC channel (normal path).
    registry.sendSync({ currentImageIndex: index });
  } else if (user.isDealer) {
    // <e3d-stream> not in DOM for a dealer: use a standalone channel so the customer
    // still receives the update in real-time (AVS-4369 fix).
    // Guard is dealer-only — customer image fetches must not broadcast to peers.
    const channel = getOrCreateStandaloneChannel();
    if (channel) {
      if (channel.isConnected()) {
        channel.send({ currentImageIndex: index });
      } else {
        // Channel is still connecting; queue the index.
        pendingIndex = index;
      }
    }
  }

  if (registry.scrollTo) {
    registry.scrollTo(index, true);
  }
}
